import React, {
  Suspense,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Divider, Flex, List, type ListRef, Spinner } from '@banhala/ui-kit';
import { useAtom } from 'jotai';
import { marketRankingFilterAtom } from './states/marketRankingFilter.atom';
import { useMarketRankingTypes } from './queries/useMarketRankingTypes';
import { useMarketRankings } from './queries/useMarketRankings';
import { MarketRankingCard } from './components/MarketRankingCard';
import {
  MarketTypeHeader,
  MarketRankingFilterChips,
} from './components/MarketRankingListHeader';
import { HomeScrollContext } from '../../context/HomeScrollContext';
import { useLogAnalyticsEvent, useOpenDeeplink } from 'shared/src/hooks';
import {
  bridge,
  getRangeForEvent,
  isNotNil,
  useNavigator,
} from 'shared/src/utils';
import {
  usePostFavoriteMarket,
  useRemoveFavoriteMarket,
} from 'shared/src/queries';
import type { MarketRankingItem } from 'shared/src/api';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { useHomeRoute } from '../../../../hooks/useHomeRoute';
import { useAutoSelectAgeFilter } from './hooks/useAutoSelectAgeFilter';
import { useLogViewMarketRanking } from './hooks/useLogViewMarketRanking';
import { useLogDisplayMarket } from './hooks/useLogDisplayMarket';
import { usePersistSelectedMarketType } from './hooks/usePersistSelectedMarketType';
import { useRegisterScrollToTop } from '../../hooks/useRegisterScrollToTop';

type ListItem =
  | { type: 'filter' }
  | { type: 'market'; data: MarketRankingItem };

export type MarketRankingHandle = { scrollToTop: () => void };

type MarketRankingProps = {
  /**
   * 외부에서 스크롤 위치를 구독. screen 단독 호스팅 시 floating button 펼침/접힘
   * 트리거에 사용. HomeScrollContext 와 병행 호출되며 둘 다 안전하게 noop 가능.
   */
  onScrollY?: (y: number) => void;
};

const MarketRankingContentImpl = (
  { onScrollY: onScrollYProp }: MarketRankingProps,
  ref: React.Ref<MarketRankingHandle>,
) => {
  const [filter, setFilter] = useAtom(marketRankingFilterAtom);
  const { data: typesData } = useMarketRankingTypes();
  const homeScroll = useContext(HomeScrollContext);
  const { openDeeplink } = useOpenDeeplink();
  const { navigator } = useNavigator();
  const route = useHomeRoute<'Home'>();

  // 딥링크로 넘어온 market_type_sno를 필터에 반영
  const deeplinkMarketTypeSno = route.params?.market_type_sno;
  usePersistSelectedMarketType(deeplinkMarketTypeSno);
  useEffect(() => {
    if (deeplinkMarketTypeSno == null) return;
    const sno = Number(deeplinkMarketTypeSno);
    if (Number.isNaN(sno)) return;
    const matched = typesData.market_types.find(t => t.sno === sno);
    if (matched == null) return;
    setFilter(prev => ({ ...prev, selectedMarketType: matched }));
  }, [deeplinkMarketTypeSno, typesData, setFilter]);

  const selectedMarketType =
    filter.selectedMarketType ?? typesData.market_types[0] ?? null;

  useAutoSelectAgeFilter(selectedMarketType?.sno ?? 0);
  useLogViewMarketRanking(selectedMarketType?.name);
  const logDisplayMarket = useLogDisplayMarket();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const { mutateAsync: postFavoriteMarket } = usePostFavoriteMarket();
  const { mutateAsync: removeFavoriteMarket } = useRemoveFavoriteMarket();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [favoriteMap, setFavoriteMap] = useState<{ [key: number]: boolean }>(
    {},
  );

  const { data, fetchNextPage, hasNextPage, refetch } = useMarketRankings({
    market_type_sno: selectedMarketType?.sno ?? 0,
    category_sno: filter.selectedCategorySno ?? undefined,
    age_list:
      filter.selectedAgeValues.length > 0
        ? filter.selectedAgeValues.filter((v): v is number => v !== null)
        : undefined,
    style_list:
      filter.selectedStyles.length > 0 ? filter.selectedStyles : undefined,
  });

  const listData = useMemo<ListItem[]>(
    () => [
      { type: 'filter' },
      ...(data?.markets ?? []).map(m => ({
        type: 'market' as const,
        data: m,
      })),
    ],
    [data?.markets],
  );

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const y = e.nativeEvent.contentOffset.y;
      homeScroll?.onScrollY(y);
      onScrollYProp?.(y);
    },
    [homeScroll, onScrollYProp],
  );

  const listRef = useRef<ListRef<ListItem>>(null);
  const handleScrollToTop = useCallback(() => {
    listRef.current?.scrollToOffset({ offset: 0, animated: true });
  }, []);
  useRegisterScrollToTop('MARKET_RANKING', handleScrollToTop);
  useImperativeHandle(ref, () => ({ scrollToTop: handleScrollToTop }), [
    handleScrollToTop,
  ]);

  const handlePressMarket = useCallback(
    (item: MarketRankingItem) => {
      const isFilteredAge = filter.selectedAgeValues.length > 0 ? '1' : '0';
      const isFilteredCategory =
        filter.selectedCategorySno !== null ? '1' : '0';
      const isFilteredStyle = filter.selectedStyles.length > 0 ? '1' : '0';
      logAnalyticsEvent({
        name: 'CLICK_MARKET',
        analytics: {
          IS_FILTERED_AGE: isFilteredAge,
          IS_FILTERED_CATEGORY: isFilteredCategory,
          IS_FILTERED_STYLE: isFilteredStyle,
          MARKET_SNO: String(item.sno),
          MARKET_NAME: item.name,
          MARKET_RANK: String(item.filtered_rank),
          MARKET_TYPE: selectedMarketType?.name,
        },
      });
      navigator.moveToMarketHome({ sno: item.sno });
    },
    [
      filter.selectedAgeValues,
      filter.selectedCategorySno,
      filter.selectedStyles,
      selectedMarketType?.name,
      logAnalyticsEvent,
      navigator,
    ],
  );

  const handlePressMarketPreview = useCallback(
    (item: MarketRankingItem) => {
      const isFilteredAge = filter.selectedAgeValues.length > 0 ? '1' : '0';
      const isFilteredCategory =
        filter.selectedCategorySno !== null ? '1' : '0';
      const isFilteredStyle = filter.selectedStyles.length > 0 ? '1' : '0';
      logAnalyticsEvent({
        name: 'CLICK_GOODS_PREVIEW',
        analytics: {
          IS_FILTERED_AGE: isFilteredAge,
          IS_FILTERED_CATEGORY: isFilteredCategory,
          IS_FILTERED_STYLE: isFilteredStyle,
          MARKET_SNO: String(item.sno),
          MARKET_NAME: item.name,
          MARKET_RANK: String(item.filtered_rank),
          MARKET_TYPE: selectedMarketType?.name,
        },
      });
      if (isNotNil(item.deeplink)) {
        openDeeplink(item.deeplink);
      }
    },
    [
      filter.selectedAgeValues,
      filter.selectedCategorySno,
      filter.selectedStyles,
      selectedMarketType?.name,
      logAnalyticsEvent,
      openDeeplink,
    ],
  );

  const handlePressFavorite = useCallback(
    async (item: MarketRankingItem) => {
      const isFavorite = favoriteMap[item.sno] ?? isNotNil(item.favorite);
      const next = !isFavorite;

      // optimistic update
      setFavoriteMap(prev => ({ ...prev, [item.sno]: next }));

      // CLICK_FAVORITE_MARKET / CLICK_UNFAVORITE_MARKET 로깅
      const eventName = next
        ? 'CLICK_FAVORITE_MARKET'
        : 'CLICK_UNFAVORITE_MARKET';
      logAnalyticsEvent({
        name: eventName,
        analytics: {
          SCREEN: 'MARKET_RANKING',
          START_SCREEN: 'MARKET_RANKING',
          EVENT_TYPE: eventName,
          AGE_TAG1: item.age_tags[0],
          AGE_TAG2: item.age_tags[1],
          STYLE_TAG1: item.style_tags[0],
          STYLE_TAG2: item.style_tags[1],
          MARKET_SNO: String(item.sno),
          MARKET_NAME: item.name,
          MARKET_TYPE: item.market_type?.name,
          FAVORITES_COUNT: item.favorites_count,
          FAVORITES_COUNT_RANGE: getRangeForEvent({
            target: item.favorites_count,
            rangeUnit: 500,
          }),
          // 누르기 전 상태 (next=true 면 즐겨찾기 추가 동작 → 현재는 0).
          IS_FAVORITED: isFavorite ? '1' : '0',
        },
        ably_only_log: {
          event_name: next ? 'favorite' : 'unfavorite',
        },
      });

      try {
        if (next) {
          await postFavoriteMarket({ sno: item.sno });
        } else {
          await removeFavoriteMarket({ sno: item.sno });
        }
        // native 동기화 (iOS/Android 의 즐겨찾기 상태와 일치)
        bridge.favoriteMarket({
          market_sno: item.sno,
          is_favorite: next,
        });
      } catch {
        // 실패 시 rollback
        setFavoriteMap(prev => ({ ...prev, [item.sno]: isFavorite }));
      }
    },
    [favoriteMap, logAnalyticsEvent, postFavoriteMarket, removeFavoriteMarket],
  );

  const renderItem = useCallback(
    ({ item }: { item: ListItem }) => {
      if (item.type === 'filter') {
        return (
          <Suspense fallback={<Spinner />}>
            <MarketRankingFilterChips
              marketTypeSno={selectedMarketType?.sno ?? 0}
            />
            {listData.length === 1 && (
              <Flex.Center height={400}>
                <Spinner />
              </Flex.Center>
            )}
          </Suspense>
        );
      }

      const market = item.data;
      return (
        <MarketRankingCard
          item={market}
          isFavorite={favoriteMap[market.sno] ?? isNotNil(market.favorite)}
          onPress={() => handlePressMarket(market)}
          onPressPreview={() => handlePressMarketPreview(market)}
          onPressFavorite={() => handlePressFavorite(market)}
        />
      );
    },
    [
      listData,
      selectedMarketType?.sno,
      favoriteMap,
      handlePressMarket,
      handlePressMarketPreview,
      handlePressFavorite,
    ],
  );

  return (
    <List<ListItem>
      ref={listRef}
      data={listData}
      renderItem={renderItem}
      keyExtractor={item =>
        item.type === 'filter' ? 'filter' : `market-${item.data.sno}`
      }
      stickyHeaderIndices={[1]}
      ItemSeparatorComponent={() => <Divider size="small" />}
      ListHeaderComponent={<MarketTypeHeader />}
      refreshing={isRefreshing}
      onRefresh={() => {
        setIsRefreshing(true);
        refetch().finally(() => setIsRefreshing(false));
      }}
      onEndReached={() => {
        if (hasNextPage) {
          fetchNextPage();
        }
      }}
      onEndReachedThreshold={1}
      onScroll={handleScroll}
      scrollEventThrottle={16}
      onViewableItemsChanged={logDisplayMarket}
      viewabilityConfig={{
        itemVisiblePercentThreshold: 50,
        minimumViewTime: 500,
      }}
    />
  );
};
const MarketRankingContent = forwardRef(MarketRankingContentImpl);

const MarketRankingImpl = (
  props: MarketRankingProps,
  ref: React.Ref<MarketRankingHandle>,
) => {
  return (
    <React.Suspense
      fallback={
        <Flex.Center>
          <Spinner />
        </Flex.Center>
      }
    >
      <MarketRankingContent ref={ref} {...props} />
    </React.Suspense>
  );
};

export const MarketRanking = forwardRef(MarketRankingImpl);
